'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import Marquee from 'react-fast-marquee';
import SectionTitle from './SectionTitle';
import SectionYearSelector from './SectionYearSelector';
import styles from './MomentsSection.module.css';
import { fetchConferenceMoments } from '../api';
import type { ConferenceSite } from '../types';

interface MomentItem {
  momentId?: string;
  imageUrl?: string;
  imageFileName?: string;
  sortOrder?: number;
}

interface MomentsSectionProps {
  data?:
    | {
        momentList?: MomentItem[];
      }
    | MomentItem[];
  title?: string;
  site?: ConferenceSite;
  years?: number[];
}

const getMomentList = (data?: MomentsSectionProps['data']) => {
  const list = Array.isArray(data) ? data : data?.momentList;
  return (list || []).filter((item) => item.imageUrl);
};

const splitRows = (items: MomentItem[]) => {
  const oddRow: MomentItem[] = [];
  const evenRow: MomentItem[] = [];
  items.forEach((item, index) => {
    if (index % 2 === 0) {
      oddRow.push(item);
      return;
    }
    evenRow.push(item);
  });
  return [oddRow, evenRow].filter((row) => row.length > 0);
};

const MomentCard = ({ item, title }: { item: MomentItem; title?: string }) => {
  const imageUrl = item.imageUrl || '';

  return (
    <a
      className={styles.card}
      href={imageUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        className={styles.image}
        src={imageUrl}
        alt={item.imageFileName || title || 'moment'}
        loading="lazy"
      />
    </a>
  );
};

const MomentRow = ({
  row,
  direction,
  title,
}: {
  row: MomentItem[];
  direction: 'left' | 'right';
  title?: string;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    const measure = () => {
      const rowWidth = rowRef.current?.clientWidth || 0;
      const contentWidth = measureRef.current?.scrollWidth || 0;
      setShouldScroll(rowWidth > 0 && contentWidth > rowWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [row]);

  if (!shouldScroll) {
    return (
      <div ref={rowRef} className={styles.row} data-static="true">
        <div ref={measureRef} className={styles.track}>
          {row.map((item, index) => (
            <MomentCard
              key={`${item.momentId || item.imageUrl}-${index}`}
              item={item}
              title={title}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={rowRef} className={styles.row}>
      <div ref={measureRef} className={styles.measureTrack} aria-hidden="true">
        {row.map((item, index) => (
          <MomentCard
            key={`measure-${item.momentId || item.imageUrl}-${index}`}
            item={item}
            title={title}
          />
        ))}
      </div>
      <Marquee
        className={styles.marquee}
        speed={24}
        direction={direction}
        pauseOnHover
        autoFill
      >
        {row.map((item, index) => (
          <MomentCard
            key={`${item.momentId || item.imageUrl}-${index}`}
            item={item}
            title={title}
          />
        ))}
      </Marquee>
    </div>
  );
};

/**
 * 精彩瞬间组件。
 */
export default function MomentsSection({
  data,
  title,
  site,
  years,
}: MomentsSectionProps) {
  const [, startTransition] = useTransition();
  const requestIdRef = useRef(0);
  const [sectionData, setSectionData] =
    useState<MomentsSectionProps['data']>(data);
  const [selectedYear, setSelectedYear] = useState(
    () => years?.[0] || new Date().getFullYear()
  );
  const moments = getMomentList(sectionData);
  const rows = useMemo(() => splitRows(moments), [moments]);

  useEffect(() => {
    setSectionData(data);
    if (years?.[0]) {
      setSelectedYear(years[0]);
    }
  }, [data, years]);

  const handleYearChange = (year: number) => {
    if (year === selectedYear) {
      return;
    }
    const previousYear = selectedYear;
    setSelectedYear(year);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    startTransition(() => {
      void fetchConferenceMoments({
        conferenceYear: year,
        site,
      })
        .then((nextData) => {
          if (requestIdRef.current === requestId) {
            setSectionData(nextData);
          }
        })
        .catch((error) => {
          console.error('[conference moments year change error]', error);
          if (requestIdRef.current === requestId) {
            setSelectedYear(previousYear);
          }
        });
    });
  };

  // 无数据时仍保留模块标题和锚点，保证顶部导航可以跳转到精彩瞬间模块。
  return (
    <section id="moments" className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <SectionTitle className="mb-12 mobile:mb-5 mobile:!text-[24px] text-black mobile:leading-[34px]">
            {title}
          </SectionTitle>
          <SectionYearSelector
            selectedYear={selectedYear}
            years={years || []}
            onChange={handleYearChange}
          />
        </div>
      </div>
      <div className={styles.stage}>
        {rows.map((row, rowIndex) => (
          <MomentRow
            key={`moment-row-${rowIndex}`}
            row={row}
            direction={rowIndex === 0 ? 'left' : 'right'}
            title={title}
          />
        ))}
      </div>
    </section>
  );
}
